import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ProtectedDeleteDialog } from "@/components/admin/ProtectedDeleteDialog";
import { formatAdminLabel } from "@/lib/adminFormatters";
import { api, getErrorMessage } from "@/lib/api";


const PermissionChecklist = ({ options, selectedPermissions, onToggle, adminRole, namespace }) => (
  <div className="grid gap-3 md:grid-cols-2">
    {options.map((option) => {
      const isLocked = adminRole !== "super_admin" && option.high_risk;
      return (
        <label key={`${namespace}-${option.code}`} className={`flex items-start gap-3 rounded-[1.1rem] border border-stone-200 bg-[#fcfaf5] p-3 ${isLocked ? "opacity-60" : ""}`} data-testid={`${namespace}-${option.code}-row`}>
          <Checkbox
            checked={selectedPermissions.includes(option.code)}
            disabled={isLocked}
            onCheckedChange={(checked) => onToggle(option.code, Boolean(checked))}
            data-testid={`${namespace}-${option.code}-checkbox`}
          />
          <span className="space-y-1">
            <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
            <span className="block text-xs leading-5 text-slate-600">{option.description}</span>
          </span>
        </label>
      );
    })}
  </div>
);


export default function AdminManagementPage({ session }) {
  const [management, setManagement] = useState({ admins: [], available_roles: [], available_permissions: [] });
  const [status, setStatus] = useState({ loading: true, error: "", success: "", emailStatus: "" });
  const [createForm, setCreateForm] = useState({ full_name: "", email: "", admin_role: "payments_admin", permissions: [] });
  const [editForms, setEditForms] = useState({});
  const [editingId, setEditingId] = useState("");

  const roleMap = useMemo(() => Object.fromEntries((management.available_roles || []).map((role) => [role.code, role])), [management.available_roles]);

  const loadManagement = async () => {
    setStatus((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await api.get("/admin/admin-users");
      setManagement(response.data);
      setStatus((current) => ({ ...current, loading: false, error: "" }));
      setCreateForm((current) => ({
        ...current,
        permissions: current.permissions.length ? current.permissions : (response.data.available_roles?.find((item) => item.code === current.admin_role)?.permissions || []),
      }));
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not load admin accounts."), success: "", emailStatus: "" });
    }
  };

  useEffect(() => {
    loadManagement();
  }, []);

  const applyRoleTemplate = (roleCode, scope, adminId = "") => {
    const permissions = roleMap[roleCode]?.permissions || [];
    if (scope === "create") {
      setCreateForm((current) => ({ ...current, admin_role: roleCode, permissions }));
      return;
    }
    setEditForms((current) => ({
      ...current,
      [adminId]: { ...current[adminId], admin_role: roleCode, permissions },
    }));
  };

  const togglePermission = (scope, code, checked, adminId = "") => {
    const update = (permissions) => checked ? [...new Set([...permissions, code])] : permissions.filter((item) => item !== code);
    if (scope === "create") {
      setCreateForm((current) => ({ ...current, permissions: update(current.permissions) }));
      return;
    }
    setEditForms((current) => ({
      ...current,
      [adminId]: { ...current[adminId], permissions: update(current[adminId].permissions) },
    }));
  };

  const startEdit = (admin) => {
    setEditingId(admin.id);
    setEditForms((current) => ({
      ...current,
      [admin.id]: {
        full_name: admin.full_name,
        admin_role: admin.admin_role,
        permissions: admin.permissions,
        is_active: admin.is_active,
      },
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/admin/admin-users", { ...createForm, email: createForm.email.trim() });
      setStatus({ loading: false, error: "", success: response.data.message, emailStatus: response.data.email_delivery_status || "" });
      setCreateForm({ full_name: "", email: "", admin_role: "payments_admin", permissions: roleMap.payments_admin?.permissions || [] });
      await loadManagement();
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not create the admin account."), success: "", emailStatus: "" });
    }
  };

  const handleSave = async (adminId) => {
    try {
      await api.patch(`/admin/admin-users/${adminId}`, editForms[adminId]);
      setStatus((current) => ({ ...current, error: "", success: "Admin account updated successfully.", emailStatus: current.emailStatus }));
      setEditingId("");
      await loadManagement();
    } catch (error) {
      setStatus((current) => ({ ...current, error: getErrorMessage(error, "We could not update the admin account.") }));
    }
  };

  const handleHardDelete = async (adminId, password) => {
    try {
      const response = await api.post(`/admin/admin-users/${adminId}/hard-delete`, { password });
      setStatus((current) => ({ ...current, error: "", success: response.data.message, emailStatus: current.emailStatus }));
      await loadManagement();
    } catch (error) {
      throw getErrorMessage(error, "We could not complete the hard delete.");
    }
  };

  const handleSendSetupResetLink = async (adminId) => {
    try {
      const response = await api.post(`/admin/admin-users/${adminId}/send-setup-reset-link`);
      setStatus((current) => ({ ...current, error: "", success: response.data.message, emailStatus: response.data.email_delivery_status || current.emailStatus }));
      await loadManagement();
    } catch (error) {
      setStatus((current) => ({ ...current, error: getErrorMessage(error, "We could not send the setup/reset email."), success: "", emailStatus: current.emailStatus }));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-management-page">
      {status.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-management-error-message">{status.error}</p>}
      {status.success && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="admin-management-success-message"><p>{status.success}</p>{status.emailStatus && <p className="mt-2 font-semibold" data-testid="admin-management-email-status-text">Email delivery status: {formatAdminLabel(status.emailStatus)}</p>}</div>}

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-management-overview-card">
        <CardContent className="flex flex-wrap items-end justify-between gap-4 p-8">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="admin-management-eyebrow">Super admin controls</p>
            <h2 className="text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950" data-testid="admin-management-title">Admin accounts and permissions</h2>
            <p className="text-[16px] leading-8 text-slate-600" data-testid="admin-management-description">Create admins, assign granular access, and send secure onboarding links without exposing any controls to applicants or students.</p>
          </div>
          <p className="text-sm leading-7 text-slate-600" data-testid="admin-management-signed-in-text">Signed in as {session?.display_name}</p>
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-management-create-card">
        <CardHeader>
          <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-management-create-title">Create admin account</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={handleCreate}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input placeholder="Full name" value={createForm.full_name} onChange={(event) => setCreateForm((current) => ({ ...current, full_name: event.target.value }))} data-testid="admin-create-full-name-input" />
              <Input placeholder="Admin email" type="email" value={createForm.email} onChange={(event) => setCreateForm((current) => ({ ...current, email: event.target.value }))} data-testid="admin-create-email-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-800" htmlFor="admin-create-role">Starting role</label>
              <select id="admin-create-role" className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={createForm.admin_role} onChange={(event) => applyRoleTemplate(event.target.value, "create")} data-testid="admin-create-role-select">
                {(management.available_roles || []).map((role) => (
                  <option key={role.code} value={role.code} disabled={!role.assignable}>{role.label}</option>
                ))}
              </select>
            </div>
            <PermissionChecklist options={management.available_permissions || []} selectedPermissions={createForm.permissions} onToggle={(code, checked) => togglePermission("create", code, checked)} adminRole={createForm.admin_role} namespace="admin-create-permission" />
            <Button type="submit" className="rounded-full" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)", color: "var(--brand-white, #FFFFFF)" }} data-testid="admin-create-submit-button">Create admin and send setup email</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-management-list-card">
        <CardHeader>
          <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-management-list-title">Current admin accounts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.loading ? <p className="text-sm text-slate-600" data-testid="admin-management-loading-text">Loading admin accounts...</p> : (management.admins || []).map((admin) => {
            const isEditing = editingId === admin.id;
            const form = editForms[admin.id] || { full_name: admin.full_name, admin_role: admin.admin_role, permissions: admin.permissions, is_active: admin.is_active };
            return (
              <div key={admin.id} className="rounded-[1.4rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid={`admin-management-card-${admin.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-slate-950" data-testid={`admin-management-name-${admin.id}`}>{admin.full_name}</p>
                    <p className="text-sm text-slate-600" data-testid={`admin-management-email-${admin.id}`}>{admin.email}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-white px-3 py-1 font-semibold text-slate-700" data-testid={`admin-management-role-${admin.id}`}>{roleMap[admin.admin_role]?.label || formatAdminLabel(admin.admin_role)}</span>
                      <span className={`rounded-full px-3 py-1 font-semibold ${admin.is_active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={`admin-management-status-${admin.id}`}>{admin.is_active ? "Active" : "Disabled"}</span>
                      {admin.must_change_password && <span className="rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700" data-testid={`admin-management-password-flag-${admin.id}`}>Password change required</span>}
                      {!admin.setup_completed && <span className="rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700" data-testid={`admin-management-setup-pending-${admin.id}`}>Setup pending</span>}
                      {admin.is_seeded_primary && <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700" data-testid={`admin-management-seed-flag-${admin.id}`}>Seeded primary super admin</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => startEdit(admin)} disabled={admin.email === session?.email} data-testid={`admin-management-edit-button-${admin.id}`}>{admin.email === session?.email ? "Signed in account" : "Edit admin"}</Button>
                    <ProtectedDeleteDialog
                      triggerLabel="Hard delete"
                      title="Hard delete admin account"
                      description={`Remove ${admin.full_name} permanently from the portal.`}
                      onConfirm={(password) => handleHardDelete(admin.id, password)}
                      testIdPrefix={`admin-hard-delete-${admin.id}`}
                      triggerDisabled={admin.email === session?.email || admin.is_seeded_primary}
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {admin.permissions.map((permission) => (
                    <span key={`${admin.id}-${permission}`} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700" data-testid={`admin-management-permission-${admin.id}-${permission}`}>{formatAdminLabel(permission)}</span>
                  ))}
                </div>
                {isEditing && (
                  <div className="mt-5 space-y-5 border-t border-stone-200 pt-5" data-testid={`admin-management-editor-${admin.id}`}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input value={form.full_name} onChange={(event) => setEditForms((current) => ({ ...current, [admin.id]: { ...current[admin.id], full_name: event.target.value } }))} data-testid={`admin-management-edit-name-input-${admin.id}`} />
                      <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" value={form.admin_role} onChange={(event) => applyRoleTemplate(event.target.value, "edit", admin.id)} data-testid={`admin-management-edit-role-select-${admin.id}`}>
                        {(management.available_roles || []).map((role) => (
                          <option key={role.code} value={role.code} disabled={!role.assignable && role.code !== form.admin_role}>{role.label}</option>
                        ))}
                      </select>
                    </div>
                    <PermissionChecklist options={management.available_permissions || []} selectedPermissions={form.permissions} onToggle={(code, checked) => togglePermission("edit", code, checked, admin.id)} adminRole={form.admin_role} namespace={`admin-edit-permission-${admin.id}`} />
                    <label className="flex items-center gap-3 text-sm text-slate-700" data-testid={`admin-management-active-toggle-row-${admin.id}`}>
                      <Checkbox checked={form.is_active} onCheckedChange={(checked) => setEditForms((current) => ({ ...current, [admin.id]: { ...current[admin.id], is_active: Boolean(checked) } }))} data-testid={`admin-management-active-toggle-${admin.id}`} />
                      Admin account active
                    </label>
                    <div className="flex flex-wrap gap-3">
                      <Button className="rounded-full" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)", color: "var(--brand-white, #FFFFFF)" }} onClick={() => handleSave(admin.id)} data-testid={`admin-management-save-button-${admin.id}`}>Save changes</Button>
                      {session?.role === "super_admin" && !admin.is_seeded_primary && (
                        <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => handleSendSetupResetLink(admin.id)} data-testid={`admin-management-send-setup-reset-link-button-${admin.id}`}>Send setup/reset link</Button>
                      )}
                      <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => setEditingId("")} data-testid={`admin-management-cancel-button-${admin.id}`}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
